import { ArrowRight, Info } from "lucide-react";
import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ScaledThumbnail } from "./ScaledThumbnail";

interface RecentTransactionsCardThumbnailProps {
  isHovered?: boolean;
  alwaysVisible?: boolean;
}

const COLUMNS: Array<{ label: string; width: string }> = [
  { label: "Date", width: "16%" },
  { label: "Description", width: "28%" },
  { label: "Category", width: "15%" },
  { label: "Status", width: "17%" },
  { label: "In", width: "12%" },
  { label: "Out", width: "12%" },
];

const ROWS = [
  {
    date: "11 Aug 2026",
    description: "Acme Corp — Invoice #1234",
    category: "Sales",
    status: "Settled",
    statusVariant: "successInverted" as const,
    in: "£12,500.00",
    out: "",
  },
  {
    date: "10 Aug 2026",
    description: "Office Supplies Ltd — Bill #5678",
    category: "Overheads",
    status: "Outstanding",
    statusVariant: "warningInverted" as const,
    in: "",
    out: "£850.00",
  },
  {
    date: "9 Aug 2026",
    description: "Tech Solutions — Invoice #9012",
    category: "Sales",
    status: "Settled",
    statusVariant: "successInverted" as const,
    in: "£8,200.00",
    out: "",
  },
];

export default function RecentTransactionsCardThumbnail({
  isHovered = false,
  alwaysVisible = false,
}: RecentTransactionsCardThumbnailProps) {
  return (
    <ScaledThumbnail
      isHovered={isHovered}
      alwaysVisible={alwaysVisible}
      contentWidth={640}
    >
      <Card className="w-[640px]">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="text-lg font-semibold text-neutral-900">
              Recent Transactions
            </span>
            <Info className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
        </div>

        <table className="w-full table-fixed border-collapse">
          <colgroup>
            {COLUMNS.map((column) => (
              <col key={column.label} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead>
            <tr className="border-b border-gray-200">
              {COLUMNS.map((column) => (
                <th
                  key={column.label}
                  className={`px-3 py-2.5 text-sm font-semibold text-neutral-900 ${
                    column.label === "In" || column.label === "Out"
                      ? "text-right"
                      : "text-left"
                  }`}
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row, index) => (
              <tr
                key={row.description}
                className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
              >
                <td className="px-3 py-2.5 text-sm text-neutral-800 truncate">
                  {row.date}
                </td>
                <td className="px-3 py-2.5 text-sm text-neutral-800 truncate">
                  {row.description}
                </td>
                <td className="px-3 py-2.5 text-sm text-neutral-800 truncate">
                  {row.category}
                </td>
                <td className="px-3 py-2.5">
                  <Badge variant={row.statusVariant} className="text-xs">
                    {row.status}
                  </Badge>
                </td>
                <td
                  className="px-3 py-2.5 text-sm text-right tabular-nums"
                  style={{ color: "var(--mc-success-icon)" }}
                >
                  {row.in}
                </td>
                <td
                  className="px-3 py-2.5 text-sm text-right tabular-nums"
                  style={{ color: "var(--mc-error-icon)" }}
                >
                  {row.out}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end border-t px-4 py-3">
          <span className="flex items-center gap-1.5 text-sm text-neutral-900">
            View all transactions
            <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Card>
    </ScaledThumbnail>
  );
}
